import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, TextField, MenuItem, ListSubheader, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, Box, IconButton, Menu, Checkbox, ListItemText,
  Divider, Tooltip, LinearProgress, Chip, alpha
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Navigate, useSearchParams } from "react-router-dom";
import {
  collection, getDocs, addDoc, deleteDoc, doc, updateDoc, query, where, serverTimestamp, writeBatch
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useAuth } from "../authContext";
import { logActivity } from "../utils/activityLogger";

const statusOptions = ["In Progress", "Completed", "Pending", "Not Applicable"];
const priorityOptions = ["High", "Medium", "Low"];

// ─── User Category Options (same taxonomy as ProjectInformationPage) ──────────
const CATEGORY_OPTIONS = [
  "Leadership (owners, managing partners)",
  "Engagement Heads (Experienced Professionals)",
  "Qualified Professionals (CA, CS, ACCA or Other Post Graduate qualifications)",
  "Semi-Qualified Staff (pursuing professional course)",
  "Trainee's (Interns etc)",
  "Support Staff (other employees)",
  "HR (internal administrative staff)",
];

// ─── Design Tokens ────────────────────────────────────────────────────────────
const T = {
  // Primary palette
  primary:    "#1a56db",
  primaryDk:  "#1344b0",
  primaryLt:  "#e8effd",
  // Surface
  bg:         "#f5f7fa",
  surface:    "#ffffff",
  surfaceAlt: "#f9fafc",
  // Borders
  border:     "#e3e8f0",
  borderMd:   "#cdd5e0",
  // Text
  textPrimary:   "#111827",
  textSecondary: "#4b5563",
  textMuted:     "#9ca3af",
  // Status colours
  statusColors: {
    "Completed":       { bg: "#d1fae5", color: "#065f46", border: "#a7f3d0" },
    "In Progress":     { bg: "#dbeafe", color: "#1e40af", border: "#bfdbfe" },
    "Pending":         { bg: "#fef3c7", color: "#92400e", border: "#fde68a" },
    "Not Applicable":  { bg: "#f3f4f6", color: "#374151", border: "#e5e7eb" },
  },
  priorityColors: {
    "High":   { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
    "Medium": { bg: "#ffedd5", color: "#9a3412", border: "#fdba74" },
    "Low":    { bg: "#f0fdf4", color: "#14532d", border: "#86efac" },
  },
  // Elevation
  shadow:   "0 1px 3px 0 rgba(0,0,0,.08), 0 1px 2px -1px rgba(0,0,0,.06)",
  shadowMd: "0 4px 6px -1px rgba(0,0,0,.08), 0 2px 4px -2px rgba(0,0,0,.05)",
  shadowLg: "0 10px 15px -3px rgba(0,0,0,.08), 0 4px 6px -4px rgba(0,0,0,.05)",
  radius: "10px",
  radiusSm: "6px",
};

// ─── Small styled helpers ─────────────────────────────────────────────────────
const StatusBadge = ({ label }) => {
  const c = T.statusColors[label] || { bg: "#f3f4f6", color: "#374151", border: "#e5e7eb" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 10px", borderRadius: 20,
      fontSize: 12, fontWeight: 600, letterSpacing: "0.01em",
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      whiteSpace: "nowrap",
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%",
        background: c.color, opacity: 0.8, flexShrink: 0,
      }} />
      {label}
    </span>
  );
};

const PriorityBadge = ({ label }) => {
  const c = T.priorityColors[label] || { bg: "#f3f4f6", color: "#374151", border: "#e5e7eb" };
  return (
    <span style={{
      display: "inline-block",
      padding: "3px 10px", borderRadius: 20,
      fontSize: 12, fontWeight: 600,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
};

const TimeLeftBadge = ({ text, bg, color }) => (
  <span style={{
    display: "inline-block",
    padding: "3px 10px", borderRadius: 20,
    fontSize: 12, fontWeight: 600,
    background: bg, color: color,
    whiteSpace: "nowrap",
  }}>
    {text}
  </span>
);

// ─── SidebarSection ───────────────────────────────────────────────────────────
// Owns its own search state so typing never re-renders the parent Tasks component
const SidebarSection = React.memo(function SidebarSection({
  title, isOpen, onToggle, activeCount, onClearSection,
  items, // [{ name, count, checked }]
  onToggleItem,
  renderItem, // optional custom renderer
  borderBottom = true,
}) {
  const [search, setSearch] = useState("");
  const inputRef = React.useRef(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return q ? items.filter(item => item.name.toLowerCase().includes(q)) : items;
  }, [items, search]);

  return (
    <Box>
      <button onClick={onToggle} style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 16px", background: "none", border: "none",
        borderBottom: `1px solid ${T.border}`,
        cursor: "pointer", fontFamily: "inherit",
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: activeCount > 0 ? T.primary : T.textPrimary, letterSpacing: "0.01em", display: "flex", alignItems: "center", gap: 6 }}>
          {title}
          {activeCount > 0 && (
            <span style={{ background: T.primary, color: "#fff", borderRadius: 10, fontSize: 10, padding: "1px 6px", fontWeight: 700 }}>{activeCount}</span>
          )}
        </span>
        {isOpen
          ? <ExpandLessIcon style={{ fontSize: 16, color: T.textMuted }} />
          : <ExpandMoreIcon style={{ fontSize: 16, color: T.textMuted }} />
        }
      </button>
      {isOpen && (
        <Box sx={{ px: 1.5, pt: 1, pb: 1.5, ...(borderBottom ? { borderBottom: `1px solid ${T.border}` } : {}) }}>
          <input
            ref={inputRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search…"
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "5px 10px", marginBottom: 6,
              fontSize: 12, borderRadius: T.radiusSm,
              border: `1px solid ${T.border}`,
              outline: "none", fontFamily: "inherit",
              color: T.textPrimary, background: T.surface,
            }}
            onFocus={e => e.target.style.borderColor = T.primary}
            onBlur={e => e.target.style.borderColor = T.border}
          />
          <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
            {filtered.map(item => renderItem ? renderItem(item) : (
              <Box key={item.name} onClick={() => onToggleItem(item.name)} title={item.name}
                sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 0.5, py: 0.25, borderRadius: T.radiusSm, cursor: "pointer", "&:hover": { background: T.primaryLt } }}>
                <Checkbox checked={item.checked} size="small" sx={{ p: 0.5, color: T.textMuted, "&.Mui-checked": { color: T.primary } }} />
                <span style={{ fontSize: 12, color: item.checked ? T.primary : T.textPrimary, fontWeight: item.checked ? 600 : 400, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
                <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0 }}>{item.count}</span>
              </Box>
            ))}
            {filtered.length === 0 && (
              <p style={{ fontSize: 12, color: T.textMuted, textAlign: "center", margin: "8px 0" }}>No matches</p>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
});

// ─── SortableHeader ───────────────────────────────────────────────────────────
const SortableHeader = ({ field, label, sortField, sortOrder, onClick, style = {}, width, onResize }) => {
  const active = sortField === field;

  const handleDrag = React.useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startW = width || 120;
    const onMove = (mv) => onResize(Math.max(60, startW + mv.clientX - startX));
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [width, onResize]);

  return (
    <TableCell
      onClick={() => onClick(field)}
      style={{
        cursor: "pointer", userSelect: "none",
        fontWeight: 700, fontSize: 12,
        color: active ? T.primary : T.textSecondary,
        letterSpacing: "0.05em", textTransform: "uppercase",
        background: T.surfaceAlt,
        borderBottom: `2px solid ${active ? T.primary : T.border}`,
        whiteSpace: "nowrap", padding: "12px 14px",
        transition: "color 0.15s",
        position: "relative",
        width: width || undefined,
        minWidth: width || undefined,
        maxWidth: width || undefined,
        overflow: "hidden",
        ...style,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {label}
        {active ? (
          <span style={{ fontSize: 11 }}>{sortOrder === "asc" ? "↑" : "↓"}</span>
        ) : (
          <span style={{ fontSize: 11, opacity: 0.3 }}>↕</span>
        )}
      </span>
      {/* Drag handle */}
      <span
        onMouseDown={handleDrag}
        onClick={e => e.stopPropagation()}
        style={{
          position: "absolute", right: 0, top: 0, bottom: 0,
          width: 6, cursor: "col-resize",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 2,
        }}
        onMouseEnter={e => e.currentTarget.firstChild.style.opacity = "1"}
        onMouseLeave={e => e.currentTarget.firstChild.style.opacity = "0"}
      >
        <span style={{
          display: "block", width: 2, height: 16,
          borderRadius: 2, background: T.primary,
          opacity: 0, transition: "opacity 0.15s",
          pointerEvents: "none",
        }} />
      </span>
    </TableCell>
  );
};

// ─── ActionButton ─────────────────────────────────────────────────────────────
const ActionBtn = ({ variant = "primary", children, disabled, onClick, component, ...rest }) => {
  const styles = {
    primary: {
      bg: T.primary, color: "#fff", border: `1px solid ${T.primary}`,
      hover: T.primaryDk,
    },
    secondary: {
      bg: T.surface, color: T.textPrimary, border: `1px solid ${T.borderMd}`,
      hover: T.bg,
    },
    danger: {
      bg: "#fff1f2", color: "#be123c", border: "1px solid #fecdd3",
      hover: "#ffe4e6",
    },
    success: {
      bg: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0",
      hover: "#dcfce7",
    },
  };
  const s = styles[variant] || styles.secondary;
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "8px 16px", borderRadius: T.radiusSm,
        fontSize: 13, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
        background: disabled ? "#f3f4f6" : s.bg,
        color: disabled ? T.textMuted : s.color,
        border: disabled ? `1px solid ${T.border}` : s.border,
        boxShadow: disabled ? "none" : T.shadow,
        transition: "all 0.15s",
        whiteSpace: "nowrap",
        fontFamily: "inherit",
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = s.hover; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = s.bg; }}
      {...rest}
    >
      {children}
    </button>
  );
};

export default function Tasks() {
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("focus") || "";
  const { companyId, user, loading } = useAuth();

  const canReview = user?.role === "Admin" || user?.role === "Manager";

  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [entities, setEntities] = useState([]);
  const [users, setUsers] = useState([]);

  const [filterEntities, setFilterEntities] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProject, setFilterProject] = useState("");
  const [filterAssignedTo, setFilterAssignedTo] = useState("");
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterTimeLeft, setFilterTimeLeft] = useState([]);
  const [filterPriority, setFilterPriority] = useState([]);
  const [filterArea, setFilterArea] = useState("");

  const [entityAnchor, setEntityAnchor] = useState(null);
  const [entityFilterValues, setEntityFilterValues] = useState([]);
  const [entityFilterSearch, setEntityFilterSearch] = useState("");

  const [dueAnchor, setDueAnchor] = useState(null);
  const [dueStart, setDueStart] = useState("");
  const [dueEnd, setDueEnd] = useState("");

  const [modAnchor, setModAnchor] = useState(null);
  const [modStart, setModStart] = useState("");
  const [modEnd, setModEnd] = useState("");

  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [entityQuery, setEntityQuery] = useState("");
  const [projectQuery, setProjectQuery] = useState("");
  const [assignedQuery, setAssignedQuery] = useState("");

  const [hfProject, setHfProject] = useState([]);
  const [hfArea, setHfArea] = useState([]);
  const [hfTask, setHfTask] = useState([]);
  const [hfAssigned, setHfAssigned] = useState([]);
  const [hfStatus, setHfStatus] = useState([]);
  const [hfPriority, setHfPriority] = useState([]);
  const [hfTimeLeft, setHfTimeLeft] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [currentTask, setCurrentTask] = useState({
    id: null, name: "", project: "", entity: "", area: "", assignedTo: "",
    priority: "", status: "", dueDate: ""
  });
  const [nameDraft, setNameDraft] = useState("");

  const [selectedTasks, setSelectedTasks] = useState([]);
  const [layoutMode, setLayoutMode] = useState("side"); // "top" | "side"
  const [sidebarWidth, setSidebarWidth] = useState(240);

  // ─── Column widths (resizable) ────────────────────────────────────────────
  const [colWidths, setColWidths] = useState({
    entity: 130, project: 160, area: 150, name: 220,
    assignedTo: 130, status: 110, priority: 90,
    dueDate: 110, _daysLeft: 130, updatedAt: 150,
    createdBy: 120,
  });
  const setColWidth = useCallback((field, w) => {
    setColWidths(prev => ({ ...prev, [field]: w }));
  }, []);
  const sidebarDragRef = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startW = sidebarWidth;
    const onMove = (mv) => {
      const next = Math.min(420, Math.max(180, startW + mv.clientX - startX));
      setSidebarWidth(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [sidebarWidth]);
  const [sidebarEntityOpen, setSidebarEntityOpen] = useState(true);
  const [sidebarProjectOpen, setSidebarProjectOpen] = useState(true);
  const [sidebarAssignedOpen, setSidebarAssignedOpen] = useState(true);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadPct, setUploadPct] = useState(0);

  // ─── User Category filter state ───────────────────────────────────────────
  const [companyUsers, setCompanyUsers] = useState([]); // { id, name, category, isActive }
  const [filterCategories, setFilterCategories] = useState([]); // selected category strings
  const [categorySearch, setCategorySearch] = useState("");
  const [categoryAnchor, setCategoryAnchor] = useState(null);

  const idNorm = (s) => (s ?? "").toString().trim().toLowerCase().replace(/[^a-z0-9]/g, "");

  const _daysLeft = (jsDate) =>
    Math.ceil((jsDate - new Date()) / (1000 * 60 * 60 * 24));

  const _timeLeftBadge = (dateStrOrISO) => {
    if (!dateStrOrISO) return { text: "—", bg: "#f3f4f6", color: "#6b7280" };
    const d = new Date(dateStrOrISO);
    if (isNaN(d)) return { text: "—", bg: "#f3f4f6", color: "#6b7280" };
    const left = _daysLeft(d);
    if (left < 0) {
      const overdueDays = Math.ceil((new Date() - d) / (1000 * 60 * 60 * 24));
      return { text: `${overdueDays} day${overdueDays === 1 ? "" : "s"} Overdue`, bg: "#fee2e2", color: "#991b1b" };
    }
    if (left === 0) return { text: "Today", bg: "#fef3c7", color: "#92400e" };
    return { text: `${left} day${left === 1 ? "" : "s"}`, bg: "#dbeafe", color: "#1e40af" };
  };

  const handleSort = (field) => {
    setSortOrder((prev) => (sortField === field ? (prev === "asc" ? "desc" : "asc") : "asc"));
    setSortField(field);
  };

  const sortedEntities = useMemo(
    () => [...entities].sort((a, b) =>
      (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
    ), [entities]
  );

  const filteredEntities = useMemo(() => {
    const q = entityQuery.trim().toLowerCase();
    if (!q) return sortedEntities;
    return sortedEntities.filter((e) => (e.name || "").toLowerCase().includes(q));
  }, [sortedEntities, entityQuery]);

  const projectOptionsForEntity = useMemo(() => {
    const sel = (currentTask.entity || "").trim().toLowerCase();
    const base = sel
      ? projects.filter((p) => (p.entity || "").trim().toLowerCase() === sel)
      : projects;
    return base.map((p) => ({ ...p, name: (p.name || "").trim() }));
  }, [projects, currentTask.entity]);

  const sortedProjectNames = useMemo(() => {
    const names = projectOptionsForEntity.map((p) => (p.name || "").trim()).filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [projectOptionsForEntity]);

  const filteredProjectNames = useMemo(() => {
    const q = projectQuery.trim().toLowerCase();
    if (!q) return sortedProjectNames;
    return sortedProjectNames.filter((n) => n.toLowerCase().includes(q));
  }, [sortedProjectNames, projectQuery]);

  const sortedUsers = useMemo(
    () => [...users].filter(Boolean).sort((a, b) => (a || "").localeCompare(b || "", undefined, { sensitivity: "base" })),
    [users]
  );

  const filteredUsers = useMemo(() => {
    const q = assignedQuery.trim().toLowerCase();
    if (!q) return sortedUsers;
    return sortedUsers.filter((u) => (u || "").toLowerCase().includes(q));
  }, [sortedUsers, assignedQuery]);

  const getEntityByProject = useCallback((projectName) => {
    if (!projects || projects.length === 0) return "";
    const project = projects.find(
      (p) => (p.name || "").trim().toLowerCase() === (projectName || "").trim().toLowerCase()
    );
    return project ? (project.entity || "") : "";
  }, [projects]);

  if (loading) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: T.bg }}>
      <Box sx={{ textAlign: "center" }}>
        <LinearProgress sx={{ width: 200, borderRadius: 4, mb: 1.5 }} />
        <Typography sx={{ color: T.textSecondary, fontSize: 14 }}>Loading tasks…</Typography>
      </Box>
    </Box>
  );
  if (!user) return <Navigate to="/login" />;

  const fetchTasks = async () => {
    if (!companyId || !user) return;
    try {
      if (user.role === "Admin") {
        const qRef = query(collection(db, "tasks"), where("companyId", "==", companyId));
        const snap = await getDocs(qRef);
        setTasks(snap.docs.map((d) => { const data = d.data(); return { id: d.id, ...data, reviewed: Boolean(data.reviewed) }; }));
        return;
      }
      if (user.role === "Manager") {
        const managerUid = user?.uid || "";
        const meNameN = (user?.name || "").trim().toLowerCase();
        const qMgr = query(collection(db, "tasks"), where("companyId", "==", companyId), where("managerId", "==", managerUid));
        const mgrSnap = await getDocs(qMgr);
        const fromMgrId = mgrSnap.docs.map((d) => { const data = d.data(); return { id: d.id, ...data, reviewed: Boolean(data.reviewed) }; });
        const pSnap = await getDocs(query(collection(db, "projects"), where("companyId", "==", companyId)));
        const managedKeys = new Set(
          pSnap.docs.map((d) => d.data())
            .filter((p) => p?.managerId === managerUid || idNorm(p?.managerName) === idNorm(user?.name))
            .map((p) => idNorm(p?.name))
        );
        let fromProjects = [];
        if (managedKeys.size) {
          const allSnap = await getDocs(query(collection(db, "tasks"), where("companyId", "==", companyId)));
          fromProjects = allSnap.docs.map((d) => { const data = d.data(); return { id: d.id, ...data, reviewed: Boolean(data.reviewed) }; }).filter((t) => managedKeys.has(idNorm(t?.project)));
        }
        const map = new Map();
        [...fromMgrId, ...fromProjects].forEach((t) => map.set(t.id, t));
        setTasks(Array.from(map.values()));
        return;
      }
      const qAll = query(collection(db, "tasks"), where("companyId", "==", companyId));
      const allSnap = await getDocs(qAll);
      setTasks(allSnap.docs.map((d) => { const data = d.data(); return { id: d.id, ...data, reviewed: Boolean(data.reviewed) }; }));
    } catch (e) { console.error("Error fetching tasks:", e); }
  };

  const fetchProjects = async () => {
    if (!companyId) return;
    try {
      const pq = query(collection(db, "projects"), where("companyId", "==", companyId));
      const snapshot = await getDocs(pq);
      setProjects(snapshot.docs.map((docx) => ({ id: docx.id, ...docx.data() })));
    } catch (error) { console.error("Error fetching projects:", error); }
  };

  const fetchUsers = async () => {
    if (!companyId) return;
    try {
      const uq = query(collection(db, "users"), where("companyId", "==", companyId));
      const snapshot = await getDocs(uq);
      setUsers(snapshot.docs.map((docx) => docx.data().name));
    } catch (error) { console.error("Error fetching users:", error); }
  };

  const fetchEntities = async () => {
    if (!companyId) return;
    try {
      const q = query(collection(db, "entities"), where("companyId", "==", companyId));
      const snapshot = await getDocs(q);
      const entityList = snapshot.docs.map((docx) => ({ id: docx.id, ...docx.data() }));
      entityList.sort((a, b) => (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" }));
      setEntities(entityList);
    } catch (error) { console.error("Error fetching entities:", error); }
  };

  const fetchCompanyUsers = async () => {
    if (!companyId) return;
    try {
      const uq = query(collection(db, "users"), where("companyId", "==", companyId));
      const snapshot = await getDocs(uq);
      const arr = snapshot.docs.map((d) => {
        const u = d.data() || {};
        return {
          id: d.id,
          name: u.name || u.displayName || u.email || "Unnamed",
          category: u.category || "",
          isActive: (u.isActive ?? true) === true,
        };
      });
      setCompanyUsers(arr);
    } catch (error) { console.error("Error fetching company users:", error); }
  };

  useEffect(() => {
    fetchTasks(); fetchProjects(); fetchUsers(); fetchEntities(); fetchCompanyUsers();
  }, [companyId, user?.name, user?.role]);

  useEffect(() => {
    const shouldOpen = searchParams.get("open") === "add";
    const prefillUser = searchParams.get("user") || "";
    if (shouldOpen && !openDialog) {
      setCurrentTask((t) => ({ ...t, assignedTo: prefillUser }));
      setNameDraft("");
      setOpenDialog(true);
    }
  }, [searchParams, openDialog]);

  const [bootedFromURL, setBootedFromURL] = useState(false);
  useEffect(() => {
    if (bootedFromURL) return;
    if (!tasks.length && !projects.length && !entities.length && !users.length) return;
    const pEntityId = searchParams.get("entityId"), pUserId = searchParams.get("userId"), pProjectId = searchParams.get("projectId");
    const pEntity = searchParams.get("entity"), pUser = searchParams.get("user"), pProject = searchParams.get("project");
    const entityNameFromId = pEntityId ? (entities.find((e) => e.id === pEntityId)?.name || null) : null;
    const projectNameFromId = pProjectId ? (projects.find((p) => p.id === pProjectId)?.name || null) : null;
    const entityName = pEntity || entityNameFromId || null;
    const userName = pUser || pUserId || null;
    const projectName = pProject || projectNameFromId || null;
    const ciHas = (arr, v) => !!v && arr.some((x) => String(x).toLowerCase() === String(v).toLowerCase());
    const ciPick = (arr, v) => (ciHas(arr, v) ? arr.find((x) => String(x).toLowerCase() === String(v).toLowerCase()) : null);
    const entityNames = entities.map((e) => e.name).filter(Boolean);
    const userNames = users.filter(Boolean);
    const projectNames = projects.map((p) => p.name).filter(Boolean);
    const ePick = ciPick(entityNames, entityName), uPick = ciPick(userNames, userName), pPick = ciPick(projectNames, projectName);
    if (ePick) setEntityFilterValues([ePick]);
    if (uPick) { setHfAssigned([uPick]); setFilterAssignedTo(uPick); }
    if (pPick) { setHfProject([pPick]); setFilterProject(pPick); }
    const fromQS = (v) => (v || "").trim().toLowerCase();
    let qsStatuses = searchParams.getAll("status");
    if (qsStatuses.length === 0) { const single = searchParams.get("status"); if (single) qsStatuses = [single]; }
    qsStatuses = qsStatuses.map(fromQS).map((s) => (s === "in%20progress" ? "in progress" : s)).map((s) => (s === "not%20applicable" ? "not applicable" : s))
      .map((s) => s === "in progress" ? "In Progress" : s === "completed" ? "Completed" : s === "pending" ? "Pending" : s === "not applicable" ? "Not Applicable" : "").filter(Boolean);
    if (qsStatuses.length) { setFilterStatus(qsStatuses); setHfStatus(qsStatuses); }
    if (ePick || uPick || pPick) setPage(1);
    setBootedFromURL(true);
  }, [bootedFromURL, searchParams, tasks.length, projects.length, entities.length, users.length, entities, projects, users]);

  const handleOpenDialog = (task = null) => {
    if (task) {
      const normalizedProject = (task.project || "").trim();
      const derivedEntity = (task.entity && task.entity.trim()) || getEntityByProject(normalizedProject) || "";
      setCurrentTask({ ...task, project: normalizedProject, entity: derivedEntity, area: task.area || "" });
      setNameDraft(task.name || "");
    } else {
      setCurrentTask({ id: null, name: "", entity: "", project: "", area: "", assignedTo: "", priority: "", status: "", dueDate: "" });
      setNameDraft("");
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentTask({ id: null, name: "", project: "", assignedTo: "", priority: "", status: "", dueDate: "" });
    setNameDraft("");
  };

  const handleSaveTask = async () => {
    const nameToSave = (nameDraft || "").trim();
    const missing = [];
    if (!currentTask.project) missing.push("Project");
    if (!nameToSave) missing.push("Task Name");
    if (!currentTask.status) missing.push("Status");
    if (!currentTask.priority) missing.push("Priority");
    if (!currentTask.assignedTo) missing.push("Assigned To");
    if (!currentTask.dueDate) missing.push("Due Date");
    if (missing.length) { alert(`Please fill: ${missing.join(", ")}`); return; }
    try {
      const pSnap = await getDocs(query(collection(db, "projects"), where("companyId", "==", companyId), where("name", "==", (currentTask.project || "").trim())));
      const proj = pSnap.empty ? null : pSnap.docs[0].data();
      const mId = proj?.managerId || "", tlId = proj?.teamLeadId || "", tlName = proj?.teamLeadName || "";
      if (currentTask.id) {
        const taskRef = doc(db, "tasks", currentTask.id);
        const oldSnap = await getDocs(query(collection(db, "tasks"), where("__name__", "==", currentTask.id)));
        const old = oldSnap.docs[0]?.data() || {};
        const payload = {
          entity: (currentTask.entity || "").trim(), name: nameToSave, project: (currentTask.project || "").trim(),
          area: currentTask.area || "", assignedTo: currentTask.assignedTo, status: currentTask.status, priority: currentTask.priority,
          dueDate: (() => { const v = currentTask.dueDate; if (!v) return ""; const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v); if (iso) return v; const [dd, mm, yyyy] = String(v).split(/[-/]/); return dd && mm && yyyy ? `${yyyy}-${mm}-${dd}` : v; })(),
          companyId, updatedAt: serverTimestamp(), managerId: mId, teamLeadId: tlId, teamLeadName: tlName,
        };
        await updateDoc(taskRef, payload);
        const keys = ["entity","project","area","name","assignedTo","status","priority","dueDate"];
        const changes = keys.filter(k => String(old?.[k] ?? "").trim() !== String(payload?.[k] ?? "").trim()).map(k => `${k}: "${old?.[k] ?? "-"}" → "${payload[k] ?? "-"}"`);
        await logActivity({ userId: user?.uid, userName: user?.name || "Unknown", companyId, action: "UPDATE", module: "Task", details: `Updated task "${nameToSave}" (id: ${currentTask.id})` + (changes.length ? `\n→ Changes: ${changes.join("; ")}` : "") });
        if ((old?.assignedTo || "") !== (payload.assignedTo || "")) await logActivity({ userId: user?.uid, userName: user?.name || "Unknown", companyId, action: "REASSIGN", module: "Task", details: `Reassigned "${nameToSave}" (id: ${currentTask.id}) → ${old?.assignedTo || "-"} → ${payload.assignedTo || "-"}` });
        if ((old?.status || "") !== (payload.status || "")) await logActivity({ userId: user?.uid, userName: user?.name || "Unknown", companyId, action: "STATUS_CHANGE", module: "Task", details: `Status change "${nameToSave}" (id: ${currentTask.id}) → ${old?.status || "-"} → ${payload.status || "-"}` });
      } else {
        const docRef = await addDoc(collection(db, "tasks"), {
          entity: currentTask.entity || "", name: nameToSave, project: currentTask.project, area: currentTask.area || "",
          assignedTo: currentTask.assignedTo, status: currentTask.status, priority: currentTask.priority,
          dueDate: (() => { const v = currentTask.dueDate; if (!v) return ""; const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v); if (iso) return v; const [dd, mm, yyyy] = String(v).split(/[-/]/); return dd && mm && yyyy ? `${yyyy}-${mm}-${dd}` : v; })(),
          companyId, createdBy: user?.name || "Unknown", updatedAt: serverTimestamp(),
          managerId: mId, teamLeadId: tlId, teamLeadName: tlName, reviewed: false, reviewedBy: "", reviewedAt: null,
        });
        await logActivity({ userId: user?.uid, userName: user?.name || "Unknown", companyId, action: "CREATE", module: "Task", details: `User ${user?.name} Created task "${nameToSave}" (id: ${docRef.id})` });
      }
      await fetchTasks();
      handleCloseDialog();
    } catch (error) { console.error("Error saving task: ", error); alert("Failed to save task."); }
  };

  const handleDeleteTask = async (id) => {
    if (!id) { alert("Invalid task ID."); return; }
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await deleteDoc(doc(db, "tasks", id));
      await logActivity({ userId: user?.uid, userName: user?.name || "Unknown", companyId, action: "DELETE", module: "Task", details: `User ${user?.name} Deleted task with ID: ${id}` });
      await fetchTasks();
      alert("Task deleted successfully.");
    } catch (error) { console.error("Error deleting task:", error); alert("Failed to delete task."); }
  };

  const handleToggleReviewed = async (task) => {
    if (!canReview || !task?.id) return;
    const next = !Boolean(task.reviewed);
    try {
      await updateDoc(doc(db, "tasks", task.id), { reviewed: next, reviewedBy: next ? (user?.name || "Unknown") : "", reviewedAt: next ? serverTimestamp() : null, updatedAt: serverTimestamp() });
      await logActivity({ userId: user?.uid, userName: user?.name || "Unknown", companyId, action: next ? "REVIEW" : "UNREVIEW", module: "Task", details: `${next ? "Reviewed" : "Un-reviewed"} task "${task.name || "-"}" (id: ${task.id})`, reviewStatus: next ? "Reviewed" : "Not Reviewed", taskName: task.name || "-", projectName: task.project || "", entityName: task.entity || "" });
      await fetchTasks();
    } catch (err) { console.error("Error toggling reviewed:", err); alert("Failed to update Reviewed status."); }
  };

  const handleBulkMarkReviewed = async () => {
    if (!canReview || selectedTasks.length === 0) return;
    if (!window.confirm(`Mark ${selectedTasks.length} selected task(s) as Reviewed?`)) return;
    try {
      const batch = writeBatch(db);
      const chosen = tasks.filter((t) => selectedTasks.includes(t.id));
      const projSet = Array.from(new Set(chosen.map((t) => (t.project || "").trim()).filter(Boolean)));
      selectedTasks.forEach((id) => { batch.update(doc(db, "tasks", id), { reviewed: true, reviewedBy: user?.name || "Unknown", reviewedAt: serverTimestamp(), updatedAt: serverTimestamp() }); });
      await batch.commit();
      await fetchTasks();
      setSelectedTasks([]);
      alert("Selected tasks marked as Reviewed.");
      const details = projSet.length === 1 ? `Reviewed ${chosen.length} task(s) under Project: ${projSet[0]}` : `Reviewed ${chosen.length} task(s) across ${projSet.length} project(s): ${projSet.slice(0, 4).join(", ")}${projSet.length > 4 ? ` +${projSet.length - 4} more` : ""}`;
      await logActivity({ userId: user?.uid, userName: user?.name || "Unknown", companyId, action: "BULK_REVIEW", module: "Task", reviewStatus: "Reviewed", reviewedCount: chosen.length, projectNames: projSet, details });
    } catch (err) { console.error("Error bulk reviewing tasks:", err); alert("Failed to bulk mark Reviewed."); }
  };

  const parseCSV = (text) => {
    const rows = []; let row = [], field = "", inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (inQuotes) { if (ch === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; } } else { field += ch; } }
      else { if (ch === '"') { inQuotes = true; } else if (ch === ",") { row.push(field); field = ""; } else if (ch === "\n") { row.push(field); rows.push(row); row = []; field = ""; } else if (ch === "\r") { } else { field += ch; } }
    }
    row.push(field);
    if (row.some((v) => String(v).trim() !== "")) rows.push(row);
    if (rows.length && rows[0].length) rows[0][0] = rows[0][0].replace(/^\uFEFF/, "");
    return rows;
  };

  const formatBytes = (bytes = 0) => { if (!bytes && bytes !== 0) return "—"; const units = ["B","KB","MB","GB"]; let i = 0, val = bytes; while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; } return `${val.toFixed(val < 10 && i > 0 ? 1 : 0)} ${units[i]}`; };

  const downloadErrorReportCSV = (rowErrors = [], originalName = "upload.csv") => {
    if (!rowErrors.length) return null;
    const ts = new Date(), pad = (n) => String(n).padStart(2, "0");
    const tsStr = `${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}_${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}`;
    const base = originalName.replace(/\.[^.]+$/, ""), fileName = `${base}_errors_${tsStr}.csv`;
    let maxIssues = 0;
    for (const item of rowErrors) { if (Array.isArray(item?.issues)) maxIssues = Math.max(maxIssues, item.issues.length); }
    const headers = ["Row"]; for (let i = 1; i <= maxIssues; i++) headers.push(`Issue ${i}`);
    const escapeCSV = (v) => { const s = (v ?? "").toString(); return (/[",\n\r]/.test(s)) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = []; lines.push(headers.map(escapeCSV).join(","));
    for (const item of rowErrors) { const rowNum = item.row, issues = item.issues || []; lines.push([rowNum, ...Array.from({ length: maxIssues }, (_, i) => issues[i] || "")].map(escapeCSV).join(",")); }
    const csv = lines.join("\n"), blob = new Blob([csv], { type: "text/csv;charset=utf-8" }), url = URL.createObjectURL(blob), a = document.createElement("a");
    a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    return fileName;
  };

  const flushUI = () => new Promise((resolve) => (typeof requestAnimationFrame === "function" ? requestAnimationFrame(() => resolve()) : setTimeout(resolve, 0)));
  const withTimeout = (promise, ms = 20000, label = "operation") => { let t; const timeout = new Promise((_, rej) => t = setTimeout(() => rej(new Error(`${label} timed out after ${ms}ms`)), ms)); return Promise.race([promise.finally(() => clearTimeout(t)), timeout]); };

  const handleFileUpload = (e) => {
    const inputEl = e.target, file = inputEl.files?.[0];
    if (!file) { alert("No file selected."); return; }
    setUploadBusy(true); setUploadStatus("Reading file…"); setUploadPct(0);
    const reader = new FileReader();
    reader.onprogress = (ev) => { if (!ev.lengthComputable) return; setUploadPct(Math.min(25, Math.round((ev.loaded / ev.total) * 25))); };
    reader.onloadstart = () => { setUploadStatus("Reading file…"); setUploadPct(0); };
    reader.onload = async (ev) => {
      try {
        setUploadStatus("Parsing CSV…");
        const text = ev.target.result || "";
        const matrix = parseCSV(text).filter(r => r.some(v => String(v).trim() !== ""));
        if (matrix.length < 2) { alert("Invalid CSV format. No data rows found."); return; }
        const headers = matrix[0].map(h => (h || "").trim()), headersL = headers.map(h => h.toLowerCase());
        const need = ["entity","project","area","name","assignedto","status","priority","duedate"];
        if (!need.every(h => headersL.includes(h))) { alert("CSV must include: entity, project, area, name, assignedTo, status, priority, dueDate"); return; }
        const idx = Object.fromEntries(headersL.map((h,i)=>[h,i])), dataRows = matrix.slice(1);
        const projMap = new Map((projects || []).map(p => [(p.name || "").trim().toLowerCase(), p]));
        const entitySet = new Set((entities || []).map(e => (e.name || "").trim().toLowerCase()));
        const userSet = new Set((users || []).map(u => (u || "").trim().toLowerCase()));
        setUploadStatus("Checking existing tasks…");
        const existingSnap = await getDocs(query(collection(db, "tasks"), where("companyId","==",companyId)));
        const existingKeyToDoc = new Map();
        existingSnap.docs.forEach(d => { const t = d.data(); existingKeyToDoc.set(`${(t.project||"").trim().toLowerCase()}|${(t.name||"").trim().toLowerCase()}`, { id: d.id, ...t }); });
        let created = 0, updated = 0, skipped = 0;
        const allErrors = [], previewErrors = [], MAX_ERRS = 6;
        const pushRowErrors = (rowNum, msgs = []) => { if (!msgs.length) return; allErrors.push({ row: rowNum, issues: msgs }); if (previewErrors.length < MAX_ERRS) previewErrors.push(`Row ${rowNum}: ${msgs.join("; ")}`); };
        const perEntity = new Map(), perProject = new Map(), perAssignee = new Map();
        const bump = (m, k) => { if (!k) return; m.set(k, (m.get(k) || 0) + 1); };
        const total = dataRows.length, basePct = 25, endPct = 95; let lastShown = 0;
        const norm = (s="") => s.toString().trim();
        const cap = (s="") => { const t = s.toString().trim().toLowerCase(); if (t === "in progress") return "In Progress"; if (t === "completed") return "Completed"; if (t === "pending") return "Pending"; if (t === "not applicable") return "Not Applicable"; if (t === "high") return "High"; if (t === "medium") return "Medium"; if (t === "low") return "Low"; return s; };
        const toISO = (raw) => { const v = norm(raw); if (!v) return ""; const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v); if (iso) return v; const parts = v.split(/[-/]/); if (parts.length === 3) { const [dd, mm, yyyy] = parts; if (yyyy && mm && dd) return `${yyyy}-${mm}-${dd}`; } return v; };
        for (let r = 0; r < total; r++) {
          const row = dataRows[r], rowNum = r + 2;
          if (r === 0 || r === total - 1 || r % 25 === 0) { setUploadStatus(`Validating & writing… ${r + 1}/${total}`); const pct = basePct + Math.round(((r + 1) / total) * (endPct - basePct)); if (pct !== lastShown) { setUploadPct(pct); lastShown = pct; } if ((r % 10) === 0) await flushUI(); }
          const entity = norm(row[idx.entity]), project = norm(row[idx.project]), area = norm(row[idx.area]), name = norm(row[idx.name]), assignedTo = norm(row[idx.assignedto] ?? row[idx["assigned to"]]), status = cap(row[idx.status]), priority = cap(row[idx.priority]), dueDate = toISO(row[idx.duedate] ?? row[idx["due date"]]);
          const rowErrors = [], missing = [];
          if (!entity) missing.push("Entity"); if (!project) missing.push("Project"); if (!name) missing.push("Task Name"); if (!status) missing.push("Status"); if (!priority) missing.push("Priority"); if (!assignedTo) missing.push("Assigned To"); if (!dueDate) missing.push("Due Date");
          if (missing.length) rowErrors.push(`Missing ${missing.join(", ")}`);
          if (entity && !entitySet.has(entity.toLowerCase())) rowErrors.push(`Entity "${entity}" not found`);
          const pMeta = project ? projMap.get(project.toLowerCase()) : null;
          if (project && !pMeta) rowErrors.push(`Project "${project}" not found`);
          if (assignedTo && !userSet.has(assignedTo.toLowerCase())) rowErrors.push(`User "${assignedTo}" not found`);
          if (status && !["In Progress","Completed","Pending","Not Applicable"].includes(status)) rowErrors.push(`Invalid status "${status}"`);
          if (priority && !["High","Medium","Low"].includes(priority)) rowErrors.push(`Invalid priority "${priority}"`);
          if (rowErrors.length) { skipped++; pushRowErrors(rowNum, rowErrors); continue; }
          const managerId = pMeta?.managerId || "", teamLeadId = pMeta?.teamLeadId || "", teamLeadName = pMeta?.teamLeadName || "";
          bump(perEntity, entity); bump(perProject, project); bump(perAssignee, assignedTo);
          const key = `${project.toLowerCase()}|${name.toLowerCase()}`, existing = existingKeyToDoc.get(key);
          if (existing?.id) { await withTimeout(updateDoc(doc(db, "tasks", existing.id), { entity, name, project, area, assignedTo, status, priority, dueDate, companyId, updatedAt: serverTimestamp(), managerId, teamLeadId, teamLeadName, reviewed: false, reviewedBy: "", reviewedAt: null }), 20000, "update"); updated++; }
          else { await withTimeout(addDoc(collection(db, "tasks"), { entity, name, project, area, assignedTo, status, priority, dueDate, companyId, createdBy: user?.name || "Unknown", updatedAt: serverTimestamp(), managerId, teamLeadId, teamLeadName, reviewed: false, reviewedBy: "", reviewedAt: null }), 20000, "create"); created++; }
        }
        setUploadStatus("Finalizing…"); setUploadPct(100);
        await fetchTasks();
        let errorFileName = null;
        if (allErrors.length) errorFileName = downloadErrorReportCSV(allErrors, file.name) || null;
        const top = (m, n=8) => [...m.entries()].sort((a,b)=>b[1]-a[1]).slice(0, n).map(([k,v]) => `${k}: ${v}`).join(", ") || "—";
        const details = `Uploaded ${file.name || "file"} (${formatBytes(file.size)}) → Parsed ${dataRows.length} rows → ${created} created, ${updated} updated, ${skipped} skipped → Entities (top): ${top(perEntity)} → Projects (top): ${top(perProject)} → Assigned To (top): ${top(perAssignee)}` + (previewErrors.length ? ` → Errors: ${previewErrors.join("; ")}` : "") + (errorFileName ? ` → Full error report: ${errorFileName}` : "");
        await logActivity({ userId: user?.uid, userName: user?.name || "Unknown", companyId, action: "UPLOAD_CSV", module: "Task", details });
        alert("CSV imported. (Full error report downloaded if there were errors.)");
      } catch (err) {
        console.error("CSV upload failed:", err);
        await logActivity({ userId: user?.uid, userName: user?.name || "Unknown", companyId, action: "UPLOAD_CSV_FAILED", module: "Task", details: `Upload failed for ${file?.name || "(no name)"} → ${String((err && err.message) || err)}` });
        alert("CSV upload failed.");
      } finally {
        if (inputEl) inputEl.value = "";
        setUploadBusy(false); setUploadStatus("");
        setTimeout(() => setUploadPct(0), 400);
      }
    };
    reader.onerror = () => { setUploadStatus("Read failed."); setUploadBusy(false); alert("Failed to read file."); };
    reader.readAsText(file);
  };

  // ─── User category lookup — must be declared BEFORE filteredSorted ───────────
  const userMetaByName = useMemo(() => {
    const m = new Map();
    companyUsers.forEach((u) => m.set(idNorm(u.name), u));
    return m;
  }, [companyUsers]);

  const visibleTasks = useMemo(() => {
    if (!tasks?.length || !user) return [];
    const meUid = user.uid || "", meName = (user.name || "").trim().toLowerCase();
    if (user.role === "Admin") return tasks;
    if (user.role === "Manager") {
      const managed = new Set((projects || []).filter((p) => p?.managerId === meUid || (p?.managerName || "").trim().toLowerCase() === meName).map((p) => (p.name || "").trim()));
      return tasks.filter((t) => managed.has((t.project || "").trim()));
    }
    const leadProjects = new Set((projects || []).filter((p) => p?.teamLeadId === meUid || (p?.teamLeadName || "").trim().toLowerCase() === meName).map((p) => (p.name || "").trim()));
    const mine = tasks.filter((t) => (t.assignedTo || "").trim().toLowerCase() === meName);
    const asLeader = tasks.filter((t) => leadProjects.has((t.project || "").trim()));
    const map = new Map();
    for (const t of mine) map.set(t.id, t);
    for (const t of asLeader) map.set(t.id, t);
    return Array.from(map.values());
  }, [tasks, projects, user?.role, user?.uid, user?.name]);

  const filteredSorted = useMemo(() => {
    if (focusId) { const one = visibleTasks.find(t => t.id === focusId); return one ? [one] : []; }
    const entityOf = (t) => (t.entity?.trim() || getEntityByProject(t.project) || "");
    let list = visibleTasks.filter((task) => {
      const entityName = entityOf(task);
      const byEntity = entityFilterValues.length === 0 || entityFilterValues.includes(entityName);
      const byProject = (hfProject.length === 0 || hfProject.includes(task.project)) && (!filterProject || task.project === filterProject);
      const byArea = (hfArea.length === 0 || hfArea.includes(task.area || "")) && (!filterArea || (task.area || "").toLowerCase().includes(filterArea.toLowerCase()));
      const byTaskName = (hfTask.length === 0 || hfTask.includes(task.name || "")) && ((task.name || "").toLowerCase().includes(searchTerm.toLowerCase()));
      const byAssigned = (hfAssigned.length === 0 || hfAssigned.includes(task.assignedTo || "")) && (!filterAssignedTo || task.assignedTo === filterAssignedTo);
      const byStatus = (hfStatus.length === 0 || hfStatus.includes(task.status)) && (filterStatus.length === 0 || filterStatus.includes(task.status));
      const byPriority = (hfPriority.length === 0 || hfPriority.includes(task.priority)) && (filterPriority.length === 0 || filterPriority.includes(task.priority));
      const byDue = (!dueStart && !dueEnd) || (task.dueDate && new Date(task.dueDate) >= new Date(`${dueStart || "1900-01-01"}T00:00:00`) && new Date(task.dueDate) <= new Date(`${dueEnd || "2999-12-31"}T23:59:59`));
      const byTimeLeft = (hfTimeLeft.length === 0 && filterTimeLeft.length === 0) || (() => {
        const days = task.dueDate ? _daysLeft(new Date(task.dueDate)) : null;
        if (days === null || isNaN(days)) return false;
        if (days < 0 && (hfTimeLeft.includes("Overdue") || filterTimeLeft.includes("Overdue"))) return true;
        if (days === 0 && (hfTimeLeft.includes("Today") || filterTimeLeft.includes("Today"))) return true;
        if (days > 0 && days <= 7 && (hfTimeLeft.includes("0–7 days") || filterTimeLeft.includes("0–7 days"))) return true;
        if (days > 7 && days <= 30 && (hfTimeLeft.includes("8–30 days") || filterTimeLeft.includes("8–30 days"))) return true;
        if (days > 30 && (hfTimeLeft.includes("30+ days") || filterTimeLeft.includes("30+ days"))) return true;
        return false;
      })();
      const upd = task.updatedAt?.toDate ? task.updatedAt.toDate() : null;
      const byMod = (!modStart && !modEnd) || (upd && upd >= new Date(`${modStart || "1900-01-01"}T00:00:00`) && upd <= new Date(`${modEnd || "2999-12-31"}T23:59:59`));

      // ─── User Category filter ─────────────────────────────────────────────
      const byCategory = filterCategories.length === 0 || (() => {
        const meta = userMetaByName.get(idNorm(task.assignedTo || ""));
        return meta ? filterCategories.includes((meta.category || "").trim()) : false;
      })();

      return byEntity && byProject && byArea && byTaskName && byAssigned && byStatus && byPriority && byDue && byMod && byTimeLeft && byCategory;
    });
    if (sortField) {
      const priorityRank = { High: 3, Medium: 2, Low: 1 };
      const statusRank = { Pending: 1, "In Progress": 2, Completed: 3, "Not Applicable": 4 };
      list = [...list].sort((a, b) => {
        const entityLower = (t) => (getEntityByProject(t.project) || "").toLowerCase();
        let valA, valB;
        switch (sortField) {
          case "entity": valA = entityLower(a); valB = entityLower(b); break;
          case "project": case "area": case "name": case "assignedTo": valA = (a[sortField] || "").toLowerCase(); valB = (b[sortField] || "").toLowerCase(); break;
          case "status": valA = statusRank[a.status] || 0; valB = statusRank[b.status] || 0; break;
          case "priority": valA = priorityRank[a.priority] || 0; valB = priorityRank[b.priority] || 0; break;
          case "dueDate": valA = a.dueDate ? new Date(a.dueDate).getTime() : 0; valB = b.dueDate ? new Date(b.dueDate).getTime() : 0; break;
          case "_daysLeft": const safeDaysLeft = (t) => t.dueDate ? _daysLeft(new Date(t.dueDate)) : Number.POSITIVE_INFINITY; valA = safeDaysLeft(a); valB = safeDaysLeft(b); break;
          case "updatedAt": valA = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : 0; valB = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : 0; break;
          default: valA = (a.name || "").toLowerCase(); valB = (b.name || "").toLowerCase();
        }
        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [visibleTasks, getEntityByProject, entityFilterValues, hfProject, hfArea, hfTask, hfAssigned, hfStatus, hfPriority, filterProject, filterArea, searchTerm, filterAssignedTo, filterStatus, filterPriority, dueStart, dueEnd, modStart, modEnd, sortField, sortOrder, focusId, hfTimeLeft, filterTimeLeft, filterCategories, userMetaByName]);

  const totalCount = filteredSorted.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);

  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalCount);
  const filteredTasks = useMemo(() => filteredSorted.slice(startIndex, endIndex), [filteredSorted, startIndex, endIndex]);

  const optionPool = filteredSorted;
  const uniqSorted = (arr) => Array.from(new Set(arr.filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const projectOpts = useMemo(() => uniqSorted(optionPool.map((t) => t.project)), [optionPool]);
  const areaOpts = useMemo(() => uniqSorted(optionPool.map((t) => t.area || "")), [optionPool]);
  const taskNameOpts = useMemo(() => uniqSorted(optionPool.map((t) => t.name || "")), [optionPool]);
  const assignedOpts = useMemo(() => uniqSorted(optionPool.map((t) => t.assignedTo || "")), [optionPool]);
  const statusOpts = useMemo(() => uniqSorted(optionPool.map((t) => t.status || "")), [optionPool]);
  const priorityOpts = useMemo(() => uniqSorted(optionPool.map((t) => t.priority || "")), [optionPool]);
  const timeLeftOpts = ["Overdue", "Today", "0–7 days", "8–30 days", "30+ days"];

  // ─── Category options: all categories actually present in user metadata ──────
  // Uses visibleTasks so options stay stable regardless of other active filters.
  // Shows exact strings from Firestore (union with CATEGORY_OPTIONS for ordering),
  // so slight spelling differences in the DB are still surfaced.
  const availableCategoryOptions = useMemo(() => {
    const cats = new Set();
    visibleTasks.forEach((t) => {
      const meta = userMetaByName.get(idNorm(t.assignedTo || ""));
      if (meta?.category) cats.add(meta.category.trim());
    });
    // Preserve CATEGORY_OPTIONS order for known categories, append any unknown ones
    const known = CATEGORY_OPTIONS.filter(c => cats.has(c));
    const unknown = [...cats].filter(c => !CATEGORY_OPTIONS.includes(c)).sort();
    return [...known, ...unknown];
  }, [visibleTasks, userMetaByName]);

  const entityOptions = useMemo(() => {
    const q = entityFilterSearch.toLowerCase();
    const names = optionPool.map((t) => t.entity?.trim() || getEntityByProject(t.project)).filter(Boolean);
    return Array.from(new Set(names)).filter((n) => n.toLowerCase().includes(q)).sort((a, b) => a.localeCompare(b));
  }, [optionPool, entityFilterSearch, getEntityByProject]);

  // ─── HeaderFilter component ───────────────────────────────────────────────
  const HeaderFilter = React.memo(function HeaderFilter({ options, selected, onChange }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [q, setQ] = useState("");
    const active = Array.isArray(selected) && selected.length > 0;
    const visible = useMemo(() => Array.from(new Set(options.filter(Boolean))).filter((v) => v.toLowerCase().includes(q.toLowerCase())).sort((a, b) => a.localeCompare(b)), [options, q]);
    const allVisibleSelected = visible.length > 0 && visible.every((v) => selected.includes(v));
    const toggle = (value) => onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 0.25, p: 0.5, borderRadius: "4px", "&:hover": { background: T.primaryLt }, ...(active ? { color: T.primary, background: T.primaryLt } : { color: T.textMuted }) }}>
          <FilterListIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} keepMounted
          PaperProps={{ style: { maxHeight: 360, width: 280, padding: 8, borderRadius: T.radius, boxShadow: T.shadowLg, border: `1px solid ${T.border}` } }}>
          <Box sx={{ p: 1, pt: 0 }}>
            <TextField placeholder="Search…" size="small" fullWidth value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.stopPropagation()}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: T.radiusSm, fontSize: 13 } }} />
          </Box>
          <Divider sx={{ my: 0.5, borderColor: T.border }} />
          <MenuItem dense onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(allVisibleSelected ? [] : visible); }}
            sx={{ borderRadius: T.radiusSm, "&:hover": { background: T.primaryLt } }}>
            <Checkbox indeterminate={selected.length > 0 && !allVisibleSelected} checked={allVisibleSelected} size="small" sx={{ color: T.primary, "&.Mui-checked": { color: T.primary } }} />
            <ListItemText primary={allVisibleSelected ? "Clear (All selected)" : "Select All (visible)"} primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
          </MenuItem>
          <Divider sx={{ my: 0.5, borderColor: T.border }} />
          {visible.map((opt) => (
            <MenuItem key={opt} dense onClick={() => toggle(opt)} sx={{ borderRadius: T.radiusSm, "&:hover": { background: T.primaryLt } }}>
              <Checkbox checked={selected.includes(opt)} size="small" sx={{ color: T.primary, "&.Mui-checked": { color: T.primary } }} />
              <ListItemText primary={opt} primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  });

  const handleClearAllFilters = () => {
    setEntityFilterValues([]); setHfProject([]); setHfArea([]); setHfTask([]); setHfAssigned([]); setHfStatus([]); setHfPriority([]);
    setFilterProject(""); setFilterArea(""); setSearchTerm(""); setFilterAssignedTo(""); setFilterStatus([]); setFilterPriority([]); setEntityFilterSearch("");
    setEntityQuery(""); setProjectQuery(""); setAssignedQuery(""); setDueStart(""); setDueEnd(""); setModStart(""); setModEnd("");
    setSortField(""); setSortOrder("asc"); setPage(1); setEntityAnchor(null); setDueAnchor(null); setModAnchor(null); setHfTimeLeft([]);
    setFilterCategories([]); setCategorySearch("");
  };

  const activeFilterCount = [
    entityFilterValues.length, hfProject.length, hfArea.length, hfTask.length, hfAssigned.length,
    hfStatus.length, hfPriority.length, hfTimeLeft.length,
    dueStart || dueEnd ? 1 : 0, modStart || modEnd ? 1 : 0,
    filterCategories.length,
  ].reduce((a, b) => a + (b > 0 ? 1 : 0), 0);

  // ─── Filter label style ───────────────────────────────────────────────────
  const filterLabelStyle = (active) => ({
    display: "inline-flex", alignItems: "center", gap: 2,
    fontSize: 12, fontWeight: 600, color: active ? T.primary : T.textSecondary,
    letterSpacing: "0.02em", textTransform: "uppercase", cursor: "default",
  });

  // ─── Table header cell style ──────────────────────────────────────────────
  const thStyle = {
    fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase",
    color: T.textSecondary, background: T.surfaceAlt,
    borderBottom: `2px solid ${T.border}`, padding: "12px 14px", whiteSpace: "nowrap",
  };

  // ─── Dialog field style ───────────────────────────────────────────────────
  const dialogFieldSx = {
    mt: 1.5,
    "& .MuiOutlinedInput-root": {
      borderRadius: T.radiusSm, fontSize: 14,
      "&:hover fieldset": { borderColor: T.primary },
      "&.Mui-focused fieldset": { borderColor: T.primary },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
  };

  return (
    <Box sx={{ minHeight: "100vh", background: T.bg, pb: 6 }}>
      {/* ─── Page Header ──────────────────────────────────────────────────── */}
      <Box sx={{
        background: T.surface, borderBottom: `1px solid ${T.border}`,
        px: { xs: 2, md: 4 }, py: 2.5,
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2,
      }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: T.textPrimary, lineHeight: 1.2, letterSpacing: "-0.02em" }}>
            Tasks
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.textMuted, mt: 0.25 }}>
            {totalCount} task{totalCount !== 1 ? "s" : ""} {activeFilterCount > 0 ? `· ${activeFilterCount} filter${activeFilterCount !== 1 ? "s" : ""} active` : ""}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          {/* CSV Upload progress */}
          {(uploadBusy || uploadPct > 0) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, background: T.primaryLt, border: `1px solid ${T.primary}20`, borderRadius: T.radiusSm, px: 2, py: 0.75, minWidth: 220 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.primary, mb: 0.5 }}>{uploadStatus || "Processing…"}</Typography>
                <LinearProgress variant="determinate" value={uploadPct} sx={{ height: 4, borderRadius: 4, background: `${T.primary}20`, "& .MuiLinearProgress-bar": { background: T.primary } }} />
              </Box>
            </Box>
          )}

          <ActionBtn variant="primary" onClick={() => handleOpenDialog()}>
            <AddIcon sx={{ fontSize: 16 }} />
            Add Task
          </ActionBtn>
          
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: T.radiusSm, fontSize: 13, fontWeight: 600, cursor: "pointer", background: T.surface, color: T.textSecondary, border: `1px solid ${T.borderMd}`, boxShadow: T.shadow, whiteSpace: "nowrap", fontFamily: "inherit", transition: "all 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
            onMouseLeave={e => e.currentTarget.style.background = T.surface}>
            <UploadFileIcon sx={{ fontSize: 16 }} />
            Upload CSV
            <input type="file" accept=".csv" style={{ display: "none" }} onChange={handleFileUpload} />
          </label>

          {canReview && (
            <ActionBtn variant="success" disabled={selectedTasks.length === 0} onClick={handleBulkMarkReviewed}>
              <CheckCircleIcon sx={{ fontSize: 15 }} />
              Mark Reviewed
              {selectedTasks.length > 0 && <span style={{ background: "#15803d", color: "#fff", borderRadius: 10, fontSize: 11, padding: "1px 6px", fontWeight: 700 }}>{selectedTasks.length}</span>}
            </ActionBtn>
          )}

          <ActionBtn
            variant="danger"
            disabled={selectedTasks.length === 0}
            onClick={async () => {
              if (!window.confirm(`Delete ${selectedTasks.length} selected tasks?`)) return;
              try {
                const toDelete = tasks.filter(t => selectedTasks.includes(t.id));
                const perEntity = new Map(), perProject = new Map(), perAssignee = new Map();
                const bump = (m, k) => { if (!k) return; m.set(k, (m.get(k) || 0) + 1); };
                for (const t of toDelete) { bump(perEntity, (t.entity || "").trim() || (getEntityByProject(t.project) || "")); bump(perProject, (t.project || "").trim()); bump(perAssignee, (t.assignedTo || "").trim()); }
                const top = (m, n=8) => [...m.entries()].sort((a,b)=>b[1]-a[1]).slice(0,n).map(([k,v])=>`${k}: ${v}`).join(", ") || "—";
                for (const id of selectedTasks) await deleteDoc(doc(db, "tasks", id));
                await fetchTasks(); setSelectedTasks([]); alert("Selected tasks deleted.");
                await logActivity({ userId: user?.uid, userName: user?.name || "Unknown", companyId, action: "BULK_DELETE", module: "Task", details: `Deleted ${toDelete.length} task(s)\n→ Entities: ${top(perEntity)}\n→ Projects: ${top(perProject)}\n→ Assigned To: ${top(perAssignee)}\n` + (toDelete.length ? `→ Sample IDs: ${toDelete.slice(0,10).map(t=>t.id).join(", ")}${toDelete.length>10?` +${toDelete.length-10} more`:""}` : "") });
              } catch (error) { console.error("Error deleting selected tasks:", error); alert("Failed to delete selected tasks."); }
            }}
          >
            <DeleteOutlineOutlinedIcon sx={{ fontSize: 15 }} />
            Delete
            {selectedTasks.length > 0 && <span style={{ background: "#be123c", color: "#fff", borderRadius: 10, fontSize: 11, padding: "1px 6px", fontWeight: 700 }}>{selectedTasks.length}</span>}
          </ActionBtn>

          {activeFilterCount > 0 && (
            <ActionBtn variant="secondary" onClick={handleClearAllFilters}>
              Clear Filters
              <span style={{ background: T.primary, color: "#fff", borderRadius: 10, fontSize: 11, padding: "1px 6px", fontWeight: 700 }}>{activeFilterCount}</span>
            </ActionBtn>
          )}

          {/* Layout toggle */}
          <Tooltip title={layoutMode === "top" ? "Switch to Side Panel layout" : "Switch to Top Bar layout"}>
            <button
              onClick={() => setLayoutMode(m => m === "top" ? "side" : "top")}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px",
                borderRadius: T.radiusSm, border: `1px solid ${T.borderMd}`,
                background: layoutMode === "side" ? T.primaryLt : T.surface,
                color: layoutMode === "side" ? T.primary : T.textSecondary,
                cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                boxShadow: T.shadow, transition: "all 0.15s",
              }}
            >
              {layoutMode === "top"
                ? <><ViewSidebarOutlinedIcon sx={{ fontSize: 16 }} /> Side Panel</>
                : <><TableRowsOutlinedIcon sx={{ fontSize: 16 }} /> Top Bar</>
              }
            </button>
          </Tooltip>

          
        </Box>
      </Box>

      {/* ─── Main Content ─────────────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2, md: 4 }, pt: 3, display: "flex", gap: 2.5, alignItems: "flex-start" }}>

        {/* ══════════════════════════════════════════════════════════════════
            SIDE PANEL (visible only in "side" layout mode)
        ══════════════════════════════════════════════════════════════════ */}
        {layoutMode === "side" && (
          <Box sx={{
            width: sidebarWidth, flexShrink: 0,
            background: T.surface, border: `1px solid ${T.border}`,
            borderRadius: T.radius, boxShadow: T.shadow,
            overflow: "hidden", position: "sticky", top: 16,
            // Needed so the drag handle sits outside the overflow:hidden
            isolation: "isolate",
          }}>
            {/* ── Drag-resize handle ─────────────────────────────────────── */}
            <Box
              onMouseDown={sidebarDragRef}
              sx={{
                position: "absolute", right: -4, top: 0, bottom: 0, width: 8,
                cursor: "col-resize", zIndex: 10,
                display: "flex", alignItems: "center", justifyContent: "center",
                "&:hover > span": { opacity: 1 },
              }}
            >
              <span style={{
                display: "block", width: 3, height: 40,
                borderRadius: 3, background: T.primary,
                opacity: 0, transition: "opacity 0.15s",
              }} />
            </Box>
            {/* Sidebar header */}
            <Box sx={{
              px: 2, py: 1.5, borderBottom: `1px solid ${T.border}`,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: T.surfaceAlt,
            }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textSecondary, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Filters
              </Typography>
              {(entityFilterValues.length > 0 || hfProject.length > 0 || hfAssigned.length > 0) && (
                <button onClick={() => { setEntityFilterValues([]); setHfProject([]); setHfAssigned([]); setFilterAssignedTo(""); setFilterProject(""); }}
                  style={{ fontSize: 11, fontWeight: 600, color: T.primary, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
                  Clear
                </button>
              )}
            </Box>

            {/* ── Entity section ─────────────────────────────────────── */}
            <SidebarSection
              title="Entity"
              isOpen={sidebarEntityOpen}
              onToggle={() => setSidebarEntityOpen(o => !o)}
              activeCount={entityFilterValues.length}
              items={entityOptions.map(name => ({
                name,
                count: optionPool.filter(t => (t.entity?.trim() || getEntityByProject(t.project)) === name).length,
                checked: entityFilterValues.includes(name),
              }))}
              onToggleItem={name => setEntityFilterValues(prev => prev.includes(name) ? prev.filter(v => v !== name) : [...prev, name])}
              borderBottom
            />

            {/* ── Project section ─────────────────────────────────────── */}
            <SidebarSection
              title="Project"
              isOpen={sidebarProjectOpen}
              onToggle={() => setSidebarProjectOpen(o => !o)}
              activeCount={hfProject.length}
              items={projectOpts.map(name => ({
                name,
                count: optionPool.filter(t => t.project === name).length,
                checked: hfProject.includes(name),
              }))}
              onToggleItem={name => setHfProject(prev => prev.includes(name) ? prev.filter(v => v !== name) : [...prev, name])}
              borderBottom
            />

            {/* ── Assigned To section ──────────────────────────────────── */}
            <SidebarSection
              title="Assigned To"
              isOpen={sidebarAssignedOpen}
              onToggle={() => setSidebarAssignedOpen(o => !o)}
              activeCount={hfAssigned.length}
              items={assignedOpts.map(name => ({
                name,
                count: optionPool.filter(t => t.assignedTo === name).length,
                checked: hfAssigned.includes(name),
              }))}
              onToggleItem={name => {
                setHfAssigned(prev => prev.includes(name) ? prev.filter(v => v !== name) : [...prev, name]);
                setFilterAssignedTo(hfAssigned.includes(name) ? "" : name);
              }}
              renderItem={item => (
                <Box key={item.name} onClick={() => {
                  setHfAssigned(prev => prev.includes(item.name) ? prev.filter(v => v !== item.name) : [...prev, item.name]);
                  setFilterAssignedTo(hfAssigned.includes(item.name) ? "" : item.name);
                }}
                  title={item.name}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 0.5, py: 0.25, borderRadius: T.radiusSm, cursor: "pointer", "&:hover": { background: T.primaryLt } }}>
                  <Checkbox checked={item.checked} size="small" sx={{ p: 0.5, color: T.textMuted, "&.Mui-checked": { color: T.primary } }} />
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flex: 1, overflow: "hidden" }}>
                    <span style={{ width: 20, height: 20, borderRadius: "50%", background: item.checked ? T.primaryLt : "#f3f4f6", color: item.checked ? T.primary : T.textMuted, fontSize: 9, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {(item.name || "?").charAt(0).toUpperCase()}
                    </span>
                    <span style={{ fontSize: 12, color: item.checked ? T.primary : T.textPrimary, fontWeight: item.checked ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
                  </span>
                  <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0 }}>{item.count}</span>
                </Box>
              )}
              borderBottom={false}
            />

            {/* ── User Category section ─────────────────────────────────── */}
            {availableCategoryOptions.length > 0 && (
              <Box sx={{ borderTop: `1px solid ${T.border}` }}>
                <button
                  onClick={() => {/* we reuse categorySearch for sidebar too */}}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "none", border: "none", cursor: "default", fontFamily: "inherit" }}
                >
                  <span style={{ fontSize: 12, fontWeight: 700, color: filterCategories.length > 0 ? T.primary : T.textPrimary, display: "flex", alignItems: "center", gap: 6 }}>
                    User Category
                    {filterCategories.length > 0 && (
                      <span style={{ background: T.primary, color: "#fff", borderRadius: 10, fontSize: 10, padding: "1px 6px", fontWeight: 700 }}>{filterCategories.length}</span>
                    )}
                  </span>
                </button>
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                  <Box sx={{ maxHeight: 220, overflowY: "auto" }}>
                    {availableCategoryOptions.map(cat => {
                      const checked = filterCategories.includes(cat);
                      const count = optionPool.filter(t => userMetaByName.get(idNorm(t.assignedTo || ""))?.category === cat).length;
                      return (
                        <Box key={cat} onClick={() => { setFilterCategories(prev => checked ? prev.filter(c => c !== cat) : [...prev, cat]); setPage(1); }}
                          sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, px: 0.5, py: 0.5, borderRadius: T.radiusSm, cursor: "pointer", "&:hover": { background: T.primaryLt } }}>
                          <Checkbox checked={checked} size="small" sx={{ p: 0.25, mt: 0.25, color: T.textMuted, "&.Mui-checked": { color: T.primary }, flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 11, color: checked ? T.primary : T.textPrimary, fontWeight: checked ? 600 : 400, lineHeight: 1.4, wordBreak: "break-word" }}>{cat}</Typography>
                            <Typography sx={{ fontSize: 10, color: T.textMuted }}>{count} task{count !== 1 ? "s" : ""}</Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Right side — filters bar + table (flex-grows to fill remaining width) */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

        {/* ─── Filters Bar ──────────────────────────────────────────────── */}
        <Box sx={{
          background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius,
          boxShadow: T.shadow, px: 2.5, py: 2, mb: 2.5,
          display: "flex", flexWrap: "wrap", gap: "6px 18px", alignItems: "center",
        }}>
          {/* Entity — hidden in side panel mode (handled by sidebar) */}
          {layoutMode === "top" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <span style={filterLabelStyle(entityFilterValues.length > 0)}>Entity</span>
            <IconButton size="small" onClick={(e) => setEntityAnchor(e.currentTarget)}
              sx={{ p: 0.5, borderRadius: "4px", "&:hover": { background: T.primaryLt }, ...(entityFilterValues.length ? { color: T.primary, background: T.primaryLt } : { color: T.textMuted }) }}>
              <FilterListIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <Menu anchorEl={entityAnchor} open={Boolean(entityAnchor)} onClose={() => setEntityAnchor(null)} keepMounted disableAutoFocusItem
              PaperProps={{ style: { maxHeight: 340, width: 280, padding: 8, borderRadius: T.radius, boxShadow: T.shadowLg, border: `1px solid ${T.border}` } }}>
              <Box sx={{ p: 1, pt: 0 }}>
                <TextField placeholder="Search entities…" size="small" fullWidth value={entityFilterSearch} onChange={(e) => setEntityFilterSearch(e.target.value)} onKeyDown={(e) => e.stopPropagation()} sx={{ "& .MuiOutlinedInput-root": { borderRadius: T.radiusSm, fontSize: 13 } }} />
              </Box>
              <Divider sx={{ my: 0.5, borderColor: T.border }} />
              <MenuItem dense onClick={(e) => { e.preventDefault(); e.stopPropagation(); const allSel = entityOptions.every((o) => entityFilterValues.includes(o)); setEntityFilterValues(allSel ? [] : entityOptions); }}
                sx={{ borderRadius: T.radiusSm, "&:hover": { background: T.primaryLt } }}>
                <Checkbox indeterminate={entityFilterValues.length > 0 && entityFilterValues.length < entityOptions.length} checked={entityOptions.length > 0 && entityFilterValues.length === entityOptions.length} size="small" sx={{ color: T.primary, "&.Mui-checked": { color: T.primary } }} />
                <ListItemText primary={entityOptions.length > 0 && entityFilterValues.length === entityOptions.length ? "Clear (All selected)" : "Select All (visible)"} primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
              </MenuItem>
              <Divider sx={{ my: 0.5, borderColor: T.border }} />
              {entityOptions.map((name) => {
                const checked = entityFilterValues.includes(name);
                return (
                  <MenuItem key={name} dense onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEntityFilterValues((prev) => (checked ? prev.filter((v) => v !== name) : [...prev, name])); }}
                    sx={{ borderRadius: T.radiusSm, "&:hover": { background: T.primaryLt } }}>
                    <Checkbox checked={checked} size="small" sx={{ color: T.primary, "&.Mui-checked": { color: T.primary } }} />
                    <ListItemText primary={name} primaryTypographyProps={{ fontSize: 13 }} />
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>
          )} {/* end layoutMode === "top" for Entity */}

          {[
            { label: "Project", opts: projectOpts, sel: hfProject, set: setHfProject, sideOnly: true },
            { label: "Area/Process", opts: areaOpts, sel: hfArea, set: setHfArea, sideOnly: false },
            { label: "Task Name", opts: taskNameOpts, sel: hfTask, set: setHfTask, sideOnly: false },
            { label: "Assigned To", opts: assignedOpts, sel: hfAssigned, set: setHfAssigned, sideOnly: true },
            { label: "Status", opts: statusOpts, sel: hfStatus, set: setHfStatus, sideOnly: false },
            { label: "Priority", opts: priorityOpts, sel: hfPriority, set: setHfPriority, sideOnly: false },
          ].filter(({ sideOnly }) => !(layoutMode === "side" && sideOnly)).map(({ label, opts, sel, set }) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <span style={filterLabelStyle(sel.length > 0)}>{label}</span>
              <HeaderFilter options={opts} selected={sel} onChange={set} />
            </Box>
          ))}

          {/* Due Date */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <span style={filterLabelStyle(!!(dueStart || dueEnd))}>Due Date</span>
            <IconButton size="small" onClick={(e) => setDueAnchor(e.currentTarget)}
              sx={{ p: 0.5, borderRadius: "4px", "&:hover": { background: T.primaryLt }, ...((dueStart || dueEnd) ? { color: T.primary, background: T.primaryLt } : { color: T.textMuted }) }}>
              <FilterListIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <Menu anchorEl={dueAnchor} open={Boolean(dueAnchor)} onClose={() => setDueAnchor(null)} keepMounted
              PaperProps={{ style: { width: 300, padding: 8, borderRadius: T.radius, boxShadow: T.shadowLg, border: `1px solid ${T.border}` } }}>
              <Box sx={{ p: 1 }}>
                <TextField label="Start" type="date" size="small" fullWidth value={dueStart} onChange={(e) => setDueStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5, ...dialogFieldSx }} />
                <TextField label="End" type="date" size="small" fullWidth value={dueEnd} onChange={(e) => setDueEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={dialogFieldSx} />
                <Divider sx={{ my: 1.5, borderColor: T.border }} />
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                  <Button size="small" onClick={() => { setDueStart(""); setDueEnd(""); }} sx={{ color: T.textSecondary, fontSize: 13 }}>Clear</Button>
                  <Button size="small" variant="contained" onClick={() => setDueAnchor(null)} sx={{ background: T.primary, borderRadius: T.radiusSm, fontSize: 13, fontWeight: 600, "&:hover": { background: T.primaryDk } }}>Apply</Button>
                </Box>
              </Box>
            </Menu>
          </Box>

          {/* Time Left */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <span style={filterLabelStyle(hfTimeLeft.length > 0)}>Time Left</span>
            <HeaderFilter options={timeLeftOpts} selected={hfTimeLeft} onChange={setHfTimeLeft} />
          </Box>

          {/* User Category */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <span style={filterLabelStyle(filterCategories.length > 0)}>User Category</span>
            <IconButton size="small" onClick={(e) => setCategoryAnchor(e.currentTarget)}
              sx={{ p: 0.5, borderRadius: "4px", "&:hover": { background: T.primaryLt }, ...(filterCategories.length ? { color: T.primary, background: T.primaryLt } : { color: T.textMuted }) }}>
              <FilterListIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <Menu anchorEl={categoryAnchor} open={Boolean(categoryAnchor)} onClose={() => setCategoryAnchor(null)} keepMounted
              PaperProps={{ style: { maxHeight: 400, width: 340, padding: 8, borderRadius: T.radius, boxShadow: T.shadowLg, border: `1px solid ${T.border}` } }}>
              <Box sx={{ p: 1, pt: 0 }}>
                <TextField placeholder="Search categories…" size="small" fullWidth value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)} onKeyDown={(e) => e.stopPropagation()}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: T.radiusSm, fontSize: 13 } }} />
              </Box>
              <Divider sx={{ my: 0.5, borderColor: T.border }} />
              {/* Select All */}
              <MenuItem dense onClick={(e) => { e.preventDefault(); e.stopPropagation();
                const visible = availableCategoryOptions.filter(c => c.toLowerCase().includes(categorySearch.toLowerCase()));
                const allSel = visible.every(c => filterCategories.includes(c));
                setFilterCategories(allSel ? [] : visible); setPage(1);
              }} sx={{ borderRadius: T.radiusSm, "&:hover": { background: T.primaryLt } }}>
                <Checkbox size="small" indeterminate={filterCategories.length > 0 && !availableCategoryOptions.every(c => filterCategories.includes(c))}
                  checked={availableCategoryOptions.length > 0 && availableCategoryOptions.every(c => filterCategories.includes(c))}
                  sx={{ color: T.primary, "&.Mui-checked": { color: T.primary } }} />
                <ListItemText primary="Select All (visible)" primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
              </MenuItem>
              <Divider sx={{ my: 0.5, borderColor: T.border }} />
              {availableCategoryOptions.filter(c => c.toLowerCase().includes(categorySearch.toLowerCase())).map(cat => {
                const checked = filterCategories.includes(cat);
                const count = optionPool.filter(t => userMetaByName.get(idNorm(t.assignedTo || ""))?.category === cat).length;
                return (
                  <MenuItem key={cat} dense onClick={() => { setFilterCategories(prev => checked ? prev.filter(c => c !== cat) : [...prev, cat]); setPage(1); }}
                    sx={{ borderRadius: T.radiusSm, "&:hover": { background: T.primaryLt }, alignItems: "flex-start" }}>
                    <Checkbox checked={checked} size="small" sx={{ color: T.primary, "&.Mui-checked": { color: T.primary }, mt: 0.25 }} />
                    <ListItemText primary={cat} secondary={`${count} task${count !== 1 ? "s" : ""}`}
                      primaryTypographyProps={{ fontSize: 13, lineHeight: 1.4 }}
                      secondaryTypographyProps={{ fontSize: 11, color: T.textMuted }} />
                  </MenuItem>
                );
              })}
              {availableCategoryOptions.filter(c => c.toLowerCase().includes(categorySearch.toLowerCase())).length === 0 && (
                <Typography sx={{ fontSize: 12, color: T.textMuted, py: 1.5, textAlign: "center" }}>No categories found</Typography>
              )}
            </Menu>
          </Box>

          {/* Last Modified */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <span style={filterLabelStyle(!!(modStart || modEnd))}>Last Modified</span>
            <IconButton size="small" onClick={(e) => setModAnchor(e.currentTarget)}
              sx={{ p: 0.5, borderRadius: "4px", "&:hover": { background: T.primaryLt }, ...((modStart || modEnd) ? { color: T.primary, background: T.primaryLt } : { color: T.textMuted }) }}>
              <FilterListIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <Menu anchorEl={modAnchor} open={Boolean(modAnchor)} onClose={() => setModAnchor(null)} keepMounted
              PaperProps={{ style: { width: 300, padding: 8, borderRadius: T.radius, boxShadow: T.shadowLg, border: `1px solid ${T.border}` } }}>
              <Box sx={{ p: 1 }}>
                <TextField label="Start" type="date" size="small" fullWidth value={modStart} onChange={(e) => setModStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5, ...dialogFieldSx }} />
                <TextField label="End" type="date" size="small" fullWidth value={modEnd} onChange={(e) => setModEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={dialogFieldSx} />
                <Divider sx={{ my: 1.5, borderColor: T.border }} />
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                  <Button size="small" onClick={() => { setModStart(""); setModEnd(""); }} sx={{ color: T.textSecondary, fontSize: 13 }}>Clear</Button>
                  <Button size="small" variant="contained" onClick={() => setModAnchor(null)} sx={{ background: T.primary, borderRadius: T.radiusSm, fontSize: 13, fontWeight: 600, "&:hover": { background: T.primaryDk } }}>Apply</Button>
                </Box>
              </Box>
            </Menu>
          </Box>

          {/* Rows per page */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
            <Typography sx={{ fontSize: 12, color: T.textSecondary, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Show</Typography>
            <TextField select size="small" value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
              sx={{ width: 72, "& .MuiOutlinedInput-root": { borderRadius: T.radiusSm, fontSize: 13 }, "& fieldset": { borderColor: T.border } }}>
              {[10, 20, 50, 100].map((n) => <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>{n}</MenuItem>)}
            </TextField>
          </Box>
        </Box>

        {/* ─── Table Card ───────────────────────────────────────────────── */}
        <Box sx={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, boxShadow: T.shadow, overflow: "hidden" }}>
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1100, borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  {/* Checkbox */}
                  <TableCell padding="checkbox" sx={{ ...thStyle, width: 48, pl: 2 }}>
                    <Checkbox
                      size="small"
                      onChange={(e) => { if (e.target.checked) { setSelectedTasks(filteredTasks.map((task) => task.id)); } else { setSelectedTasks([]); } }}
                      checked={selectedTasks.length > 0 && selectedTasks.length === filteredTasks.length}
                      indeterminate={selectedTasks.length > 0 && selectedTasks.length < filteredTasks.length}
                      sx={{ color: T.textMuted, "&.Mui-checked": { color: T.primary }, "&.MuiCheckbox-indeterminate": { color: T.primary } }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...thStyle, width: 48 }}>#</TableCell>
                  {layoutMode !== "side" && <SortableHeader field="entity" label="Entity" sortField={sortField} sortOrder={sortOrder} onClick={handleSort} width={colWidths.entity} onResize={w => setColWidth("entity", w)} />}
                  {layoutMode !== "side" && <SortableHeader field="project" label="Project" sortField={sortField} sortOrder={sortOrder} onClick={handleSort} width={colWidths.project} onResize={w => setColWidth("project", w)} />}
                  <SortableHeader field="area" label="Area / Process" sortField={sortField} sortOrder={sortOrder} onClick={handleSort} width={colWidths.area} onResize={w => setColWidth("area", w)} />
                  <SortableHeader field="name" label="Task Name" sortField={sortField} sortOrder={sortOrder} onClick={handleSort} width={colWidths.name} onResize={w => setColWidth("name", w)} />
                  <SortableHeader field="assignedTo" label="Assigned To" sortField={sortField} sortOrder={sortOrder} onClick={handleSort} width={colWidths.assignedTo} onResize={w => setColWidth("assignedTo", w)} />
                  <SortableHeader field="status" label="Status" sortField={sortField} sortOrder={sortOrder} onClick={handleSort} width={colWidths.status} onResize={w => setColWidth("status", w)} />
                  <SortableHeader field="priority" label="Priority" sortField={sortField} sortOrder={sortOrder} onClick={handleSort} width={colWidths.priority} onResize={w => setColWidth("priority", w)} />
                  <SortableHeader field="dueDate" label="Due Date" sortField={sortField} sortOrder={sortOrder} onClick={handleSort} width={colWidths.dueDate} onResize={w => setColWidth("dueDate", w)} />
                  <SortableHeader field="_daysLeft" label="Time Left" sortField={sortField} sortOrder={sortOrder} onClick={handleSort} width={colWidths._daysLeft} onResize={w => setColWidth("_daysLeft", w)} />
                  <SortableHeader field="updatedAt" label="Last Modified" sortField={sortField} sortOrder={sortOrder} onClick={handleSort} width={colWidths.updatedAt} onResize={w => setColWidth("updatedAt", w)} />
                  <TableCell sx={{ ...thStyle, width: colWidths.createdBy, minWidth: colWidths.createdBy, maxWidth: colWidths.createdBy, position: "relative", overflow: "hidden" }}>
                    Created By
                    <span onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); const sx = e.clientX, sw = colWidths.createdBy; const mv = m => setColWidth("createdBy", Math.max(60, sw + m.clientX - sx)); const up = () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); document.body.style.cursor=""; document.body.style.userSelect=""; }; document.body.style.cursor="col-resize"; document.body.style.userSelect="none"; window.addEventListener("mousemove", mv); window.addEventListener("mouseup", up); }}
                      style={{ position:"absolute", right:0, top:0, bottom:0, width:6, cursor:"col-resize", zIndex:2 }} />
                  </TableCell>
                  <TableCell sx={{ ...thStyle, width: 90 }}>Actions</TableCell>
                  <TableCell sx={{ ...thStyle, minWidth: 120 }}>Reviewed</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredTasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={layoutMode === "side" ? 13 : 15} sx={{ textAlign: "center", py: 8, color: T.textMuted, fontSize: 14 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                        <span style={{ fontSize: 36 }}>📋</span>
                        <Typography sx={{ fontWeight: 600, color: T.textSecondary }}>No tasks found</Typography>
                        <Typography sx={{ fontSize: 13, color: T.textMuted }}>Try adjusting your filters or add a new task.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredTasks.map((task, idx) => {
                  const isSelected = selectedTasks.includes(task.id);
                  return (
                    <TableRow key={task.id}
                      sx={{
                        background: isSelected ? T.primaryLt : "transparent",
                        "&:hover": { background: isSelected ? T.primaryLt : T.surfaceAlt },
                        "& td": { borderBottom: `1px solid ${T.border}`, verticalAlign: "top", padding: "11px 14px", fontSize: 13, color: T.textPrimary },
                        transition: "background 0.1s",
                      }}
                    >
                      <TableCell padding="checkbox" sx={{ pl: "16px !important" }}>
                        <Checkbox size="small" checked={isSelected}
                          onChange={() => { if (isSelected) { setSelectedTasks(selectedTasks.filter((id) => id !== task.id)); } else { setSelectedTasks((prev) => (prev.includes(task.id) ? prev : [...prev, task.id])); } }}
                          sx={{ color: T.textMuted, "&.Mui-checked": { color: T.primary } }} />
                      </TableCell>

                      <TableCell sx={{ color: `${T.textMuted} !important`, fontWeight: 600, fontSize: "12px !important" }}>
                        {startIndex + idx + 1}
                      </TableCell>

                      {layoutMode !== "side" && <TableCell sx={{ fontWeight: 500 }}>{(task.entity?.trim() || getEntityByProject(task.project) || "—")}</TableCell>}
                      {layoutMode !== "side" && (
                        <TableCell>
                          <span style={{ fontWeight: 600, color: T.primary }}>{task.project || "—"}</span>
                        </TableCell>
                      )}
                      <TableCell sx={{ color: `${T.textSecondary} !important` }}>{task.area || "—"}</TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <span style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.5 }}>
                          {task.name}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <span style={{ width: 24, height: 24, borderRadius: "50%", background: T.primaryLt, color: T.primary, fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {(task.assignedTo || "?").charAt(0).toUpperCase()}
                          </span>
                          <span style={{ fontWeight: 500 }}>{task.assignedTo || "—"}</span>
                        </span>
                      </TableCell>

                      <TableCell><StatusBadge label={task.status} /></TableCell>
                      <TableCell><PriorityBadge label={task.priority} /></TableCell>

                      <TableCell sx={{ fontWeight: 500, color: `${T.textSecondary} !important` }}>
                        {task.dueDate ? (() => { const d = new Date(task.dueDate); return `${String(d.getDate()).padStart(2,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()}`; })() : "—"}
                      </TableCell>

                      <TableCell>
                        {(() => { const b = _timeLeftBadge(task.dueDate); return <TimeLeftBadge {...b} />; })()}
                      </TableCell>

                      <TableCell sx={{ color: `${T.textMuted} !important`, fontSize: "12px !important" }}>
                        {task.updatedAt?.toDate ? (() => { const d = task.updatedAt.toDate(); return `${String(d.getDate()).padStart(2,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`; })() : "—"}
                      </TableCell>

                      <TableCell sx={{ color: `${T.textMuted} !important`, fontSize: "12px !important" }}>{task.createdBy || "—"}</TableCell>

                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Edit task">
                            <IconButton size="small" onClick={() => handleOpenDialog(task)}
                              sx={{ color: T.textSecondary, borderRadius: "6px", p: 0.75, "&:hover": { background: T.primaryLt, color: T.primary } }}>
                              <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete task">
                            <IconButton size="small" onClick={() => handleDeleteTask(task.id)}
                              sx={{ color: T.textSecondary, borderRadius: "6px", p: 0.75, "&:hover": { background: "#fee2e2", color: "#dc2626" } }}>
                              <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Tooltip title={canReview ? "Click to toggle reviewed status" : "Reviewed status (view only)"}>
                          <span
                            onClick={() => canReview && handleToggleReviewed(task)}
                            style={{ display: "inline-block", cursor: canReview ? "pointer" : "not-allowed", opacity: canReview ? 1 : 0.65 }}
                          >
                            {Boolean(task.reviewed) ? (
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: 5,
                                padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                                background: "#d1fae5", color: "#065f46", border: "1px solid #a7f3d0",
                              }}>
                                <CheckCircleIcon sx={{ fontSize: 13 }} /> Reviewed
                              </span>
                            ) : (
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: 5,
                                padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                                background: "#f9fafb", color: "#6b7280", border: "1px solid #e5e7eb",
                              }}>
                                <RadioButtonUncheckedIcon sx={{ fontSize: 13 }} /> Not Reviewed
                              </span>
                            )}
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {/* ─── Pagination ──────────────────────────────────────────────── */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 2.5, py: 1.75, borderTop: `1px solid ${T.border}`, background: T.surfaceAlt,
          }}>
            <Typography sx={{ fontSize: 13, color: T.textSecondary }}>
              Showing <strong>{totalCount === 0 ? 0 : startIndex + 1}</strong> to <strong>{endIndex}</strong> of <strong>{totalCount}</strong> entries
              {selectedTasks.length > 0 && <span style={{ marginLeft: 12, color: T.primary, fontWeight: 600 }}>· {selectedTasks.length} selected</span>}
            </Typography>

            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              {[
                { key: "prev", label: "‹", disabled: page <= 1, onClick: () => setPage((p) => Math.max(1, p - 1)) },
              ].map(b => (
                <button key={b.key} disabled={b.disabled} onClick={b.onClick} style={{
                  width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center",
                  borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: b.disabled ? T.bg : T.surface,
                  color: b.disabled ? T.textMuted : T.textPrimary, cursor: b.disabled ? "not-allowed" : "pointer",
                  fontSize: 15, fontFamily: "inherit",
                }}>{b.label}</button>
              ))}

              {(() => {
                const WINDOW = 15;
                let start = Math.max(1, page - Math.floor(WINDOW / 2));
                let end = start + WINDOW - 1;
                if (end > pageCount) { end = pageCount; start = Math.max(1, end - WINDOW + 1); }
                const buttons = [];
                if (start > 1) {
                  buttons.push(<button key="first" onClick={() => setPage(1)} style={{ width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.surface, color: T.textPrimary, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>1</button>);
                  if (start > 2) buttons.push(<button key="prevWin" onClick={() => setPage(Math.max(1, start - WINDOW))} style={{ width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.surface, color: T.textSecondary, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>…</button>);
                }
                for (let p = start; p <= end; p++) {
                  buttons.push(<button key={p} onClick={() => setPage(p)} style={{ width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: T.radiusSm, border: `1px solid ${p === page ? T.primary : T.border}`, background: p === page ? T.primary : T.surface, color: p === page ? "#fff" : T.textPrimary, cursor: "pointer", fontSize: 13, fontWeight: p === page ? 700 : 400, fontFamily: "inherit" }}>{p}</button>);
                }
                if (end < pageCount) {
                  if (end < pageCount - 1) buttons.push(<button key="nextWin" onClick={() => setPage(Math.min(pageCount, end + 1))} style={{ width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.surface, color: T.textSecondary, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>…</button>);
                  buttons.push(<button key="last" onClick={() => setPage(pageCount)} style={{ width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.surface, color: T.textPrimary, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>{pageCount}</button>);
                }
                return buttons;
              })()}

              <button disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} style={{
                width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center",
                borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: page >= pageCount ? T.bg : T.surface,
                color: page >= pageCount ? T.textMuted : T.textPrimary, cursor: page >= pageCount ? "not-allowed" : "pointer",
                fontSize: 15, fontFamily: "inherit",
              }}>›</button>
            </Box>
          </Box>
        </Box> {/* end Table Card */}
        </Box> {/* end right-side flex child */}
      </Box> {/* end main content flex row */}

      {/* ─── Add / Edit Dialog ───────────────────────────────────────────── */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: T.radius, boxShadow: T.shadowLg, border: `1px solid ${T.border}` } }}>
        <DialogTitle sx={{
          pb: 1.5, pt: 2.5, px: 3,
          fontWeight: 800, fontSize: 17, color: T.textPrimary, letterSpacing: "-0.02em",
          borderBottom: `1px solid ${T.border}`,
        }}>
          {currentTask?.id ? "Edit Task" : "Add New Task"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: "20px !important", pb: 1 }}>
          {/* Entity */}
          <TextField select fullWidth label="Entity" size="small" value={currentTask.entity || ""}
            onChange={(e) => setCurrentTask((t) => ({ ...t, entity: e.target.value }))}
            SelectProps={{ onOpen: () => setEntityQuery(""), MenuProps: { PaperProps: { sx: { maxHeight: 360, borderRadius: T.radius, boxShadow: T.shadowLg } } } }}
            sx={dialogFieldSx}>
            <ListSubheader sx={{ position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper", p: 1 }}>
              <TextField autoFocus size="small" placeholder="Search entities…" value={entityQuery} onChange={(e) => setEntityQuery(e.target.value)} onKeyDown={(e) => e.stopPropagation()} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: T.radiusSm } }} />
            </ListSubheader>
            {filteredEntities.length ? filteredEntities.map((entity) => <MenuItem key={entity.id} value={entity.name} sx={{ fontSize: 14 }}>{entity.name}</MenuItem>) : <MenuItem disabled>No matches</MenuItem>}
          </TextField>

          {/* Project */}
          <TextField select label="Project" fullWidth sx={dialogFieldSx} size="small" value={currentTask.project}
            onChange={(e) => { const proj = e.target.value; const ent = getEntityByProject(proj) || currentTask.entity || ""; setCurrentTask({ ...currentTask, project: proj, entity: ent }); }}
            SelectProps={{ onOpen: () => setProjectQuery(""), MenuProps: { PaperProps: { sx: { maxHeight: 360, borderRadius: T.radius, boxShadow: T.shadowLg } } } }}>
            <ListSubheader sx={{ position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper", p: 1 }}>
              <TextField autoFocus size="small" placeholder="Search projects…" value={projectQuery} onChange={(e) => setProjectQuery(e.target.value)} onKeyDown={(e) => e.stopPropagation()} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: T.radiusSm } }} />
            </ListSubheader>
            {filteredProjectNames.length ? filteredProjectNames.map((name) => <MenuItem key={name} value={name} sx={{ fontSize: 14 }}>{name}</MenuItem>) : <MenuItem disabled>No matches</MenuItem>}
            {currentTask.project && !filteredProjectNames.some((n) => n.toLowerCase() === currentTask.project.trim().toLowerCase()) && <MenuItem value={currentTask.project} sx={{ fontSize: 14 }}>{currentTask.project}</MenuItem>}
          </TextField>

          <TextField label="Area / Process" fullWidth sx={dialogFieldSx} size="small" margin="dense" value={currentTask.area || ""} onChange={(e) => setCurrentTask({ ...currentTask, area: e.target.value })} />
          <TextField label="Task Name" fullWidth sx={dialogFieldSx} size="small" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} />

          <TextField select label="Status" fullWidth sx={dialogFieldSx} size="small" value={currentTask.status} onChange={(e) => setCurrentTask({ ...currentTask, status: e.target.value })}>
            {statusOptions.map((status) => <MenuItem key={status} value={status} sx={{ fontSize: 14 }}>{status}</MenuItem>)}
          </TextField>

          <TextField label="Due Date" type="date" fullWidth sx={dialogFieldSx} size="small" required InputLabelProps={{ shrink: true }}
            value={(() => { const v = currentTask.dueDate; if (!v) return ""; const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v); if (iso) return v; const [dd, mm, yyyy] = String(v).split(/[-/]/); return dd && mm && yyyy ? `${yyyy}-${mm}-${dd}` : ""; })()}
            onChange={(e) => setCurrentTask({ ...currentTask, dueDate: e.target.value })} />

          <TextField select label="Assigned To" fullWidth sx={dialogFieldSx} size="small" value={currentTask.assignedTo}
            onChange={(e) => setCurrentTask({ ...currentTask, assignedTo: e.target.value })}
            SelectProps={{ onOpen: () => setAssignedQuery(""), MenuProps: { PaperProps: { sx: { maxHeight: 360, borderRadius: T.radius, boxShadow: T.shadowLg } } } }}>
            <ListSubheader sx={{ position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper", p: 1 }}>
              <TextField autoFocus size="small" placeholder="Search people…" value={assignedQuery} onChange={(e) => setAssignedQuery(e.target.value)} onKeyDown={(e) => e.stopPropagation()} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: T.radiusSm } }} />
            </ListSubheader>
            {filteredUsers.length ? filteredUsers.map((u) => <MenuItem key={u} value={u} sx={{ fontSize: 14 }}>{u}</MenuItem>) : <MenuItem disabled>No matches</MenuItem>}
            {currentTask.assignedTo && !filteredUsers.some((u) => (u || "").toLowerCase() === currentTask.assignedTo.trim().toLowerCase()) && <MenuItem value={currentTask.assignedTo} sx={{ fontSize: 14 }}>{currentTask.assignedTo}</MenuItem>}
          </TextField>

          <TextField select label="Priority" fullWidth sx={{ ...dialogFieldSx, mb: 1 }} size="small" value={currentTask.priority} onChange={(e) => setCurrentTask({ ...currentTask, priority: e.target.value })}>
            {priorityOptions.map((p) => <MenuItem key={p} value={p} sx={{ fontSize: 14 }}>{p}</MenuItem>)}
          </TextField>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${T.border}`, gap: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ color: T.textSecondary, fontSize: 13, fontWeight: 600, borderRadius: T.radiusSm, border: `1px solid ${T.border}`, px: 2, "&:hover": { background: T.bg } }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveTask}
            sx={{ background: T.primary, borderRadius: T.radiusSm, fontSize: 13, fontWeight: 700, px: 3, boxShadow: "none", "&:hover": { background: T.primaryDk, boxShadow: "none" } }}>
            {currentTask?.id ? "Update Task" : "Add Task"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
